import { AlertTriangle, Ban, Clock, Inbox, RefreshCw, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Estados de sistema.
 *
 * Regra herdada do handoff: todo estado vazio ou de erro diz **o que fazer em
 * seguida**. Uma tela que só informa "nenhum resultado" deixa o usuário parado
 * sem saber se quebrou ou se está certo.
 *
 * Erro nunca mostra detalhe técnico — mostra um código que o usuário cita no
 * suporte e que a gente encontra no log.
 */

function Moldura({ children, tracejado = false }: { children: ReactNode; tracejado?: boolean }) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 rounded-[var(--radius-card)] px-6 py-10 text-center ${
        tracejado
          ? 'border border-dashed border-borda-input bg-transparent'
          : 'border border-borda bg-superficie shadow-[var(--shadow-card)]'
      }`}
    >
      {children}
    </div>
  );
}

export function Carregando({ texto = 'Carregando...' }: { texto?: string }) {
  return (
    <Moldura>
      <div
        className="h-7 w-7 animate-spin rounded-full border-2 border-borda border-t-primaria"
        role="status"
        aria-label="Carregando"
      />
      <p className="text-[length:var(--text-secundario)] text-texto-secundario">{texto}</p>
    </Moldura>
  );
}

export function Vazio({
  titulo,
  orientacao,
  acao,
}: {
  titulo: string;
  orientacao: string;
  acao?: ReactNode;
}) {
  return (
    <Moldura tracejado>
      <Inbox size={22} className="text-texto-terciario" aria-hidden />
      <div>
        <p className="font-semibold text-texto">{titulo}</p>
        <p className="mt-1 text-[length:var(--text-secundario)] text-texto-secundario">
          {orientacao}
        </p>
      </div>
      {acao}
    </Moldura>
  );
}

export function ErroSeguro({
  codigoSuporte,
  aoTentarNovamente,
}: {
  codigoSuporte?: string | undefined;
  aoTentarNovamente?: () => void;
}) {
  return (
    <Moldura>
      <AlertTriangle size={22} className="text-saida" aria-hidden />
      <div>
        <p className="font-semibold text-texto">Algo não saiu como esperado</p>
        <p className="mt-1 text-[length:var(--text-secundario)] text-texto-secundario">
          Tente novamente em instantes. Se continuar, fale com o suporte
          {codigoSuporte ? ' citando o código abaixo' : ''}.
        </p>
        {codigoSuporte && (
          <p className="mono mt-2 text-[length:var(--text-metadado)] text-texto-terciario">
            {codigoSuporte}
          </p>
        )}
      </div>
      {aoTentarNovamente && (
        <button
          type="button"
          onClick={aoTentarNovamente}
          className="inline-flex items-center gap-2 rounded-[var(--radius-controle)] border border-borda-input px-3 py-2 text-[length:var(--text-secundario)] text-texto-secundario hover:border-primaria hover:text-primaria"
        >
          <RefreshCw size={13} aria-hidden />
          Tentar novamente
        </button>
      )}
    </Moldura>
  );
}

export function AcessoNegado() {
  return (
    <Moldura>
      <Ban size={22} className="text-texto-terciario" aria-hidden />
      <div>
        <p className="font-semibold text-texto">Área restrita</p>
        <p className="mt-1 text-[length:var(--text-secundario)] text-texto-secundario">
          Esta área é exclusiva de administradores. Fale com quem administra a conta.
        </p>
      </div>
    </Moldura>
  );
}

export function SessaoExpirada({ aoEntrar }: { aoEntrar: () => void }) {
  return (
    <Moldura>
      <Clock size={22} className="text-pendente" aria-hidden />
      <div>
        <p className="font-semibold text-texto">Sua sessão expirou</p>
        <p className="mt-1 text-[length:var(--text-secundario)] text-texto-secundario">
          Por segurança, encerramos sessões paradas há muito tempo.
        </p>
      </div>
      <button
        type="button"
        onClick={aoEntrar}
        className="rounded-[var(--radius-controle)] bg-primaria px-4 py-2 text-[length:var(--text-secundario)] font-medium text-white hover:bg-primaria-hover"
      >
        Entrar novamente
      </button>
    </Moldura>
  );
}

/**
 * Banner de WhatsApp desconectado.
 *
 * Sticky e vermelho porque a consequência é grave e silenciosa: sem conexão o
 * usuário continua mandando mensagem e nada é registrado. Ele precisa descobrir
 * pelo painel, não pela falta de lançamentos no fim do mês.
 */
export function BannerDesconectado({ aoIrParaConexao }: { aoIrParaConexao: () => void }) {
  return (
    <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b border-saida-borda bg-saida px-5 py-3 text-white">
      <div className="flex items-center gap-2">
        <WifiOff size={16} aria-hidden />
        <span className="text-[length:var(--text-secundario)] font-semibold">
          WHATSAPP DESCONECTADO
        </span>
        <span className="text-[length:var(--text-secundario)] opacity-90">
          — seus lançamentos não estão sendo recebidos
        </span>
      </div>
      <button
        type="button"
        onClick={aoIrParaConexao}
        className="rounded-[var(--radius-controle)] bg-white/15 px-3 py-1.5 text-[length:var(--text-secundario)] font-medium hover:bg-white/25"
      >
        Ir para Conexão →
      </button>
    </div>
  );
}
